import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Fade,
  LinearProgress,
  IconButton,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  SportsScore as ScoreIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
} from "@mui/icons-material";
import { Tournament, KnockoutMatch } from "../types";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import { getBracketNames } from "../utils/bracketNames";
import { withByeCards } from "../utils/scheduleUtils";
import padelEagleLogo from "../img/padelEagle.png";
import splLogo from "../img/SPL_Logo.png";

type DisplayMode = "slideshow" | "scroll" | "tabs";

// Scales its content down (never up) so it fits between its top edge and the bottom of the
// viewport (minus the slideshow control bar), so the slide never needs scrolling.
const FitToScreen: React.FC<{ children: React.ReactNode; minScale?: number; maxScale?: number }> = ({ children, minScale = 0.3, maxScale = 1 }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ scale: number; height?: number }>({ scale: 1 });

  useEffect(() => {
    const update = () => {
      const outer = outerRef.current;
      const inner = innerRef.current;
      if (!outer || !inner) return;
      const contentHeight = inner.scrollHeight;
      if (!contentHeight) return;
      const top = outer.getBoundingClientRect().top + window.scrollY;
      const available = window.innerHeight - top - 72;
      const scale = Math.max(minScale, Math.min(maxScale, available / contentHeight));
      setFit((prev) =>
        Math.abs(prev.scale - scale) < 0.005 && prev.height !== undefined
          ? prev
          : { scale, height: contentHeight * scale },
      );
    };
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (observer && innerRef.current) observer.observe(innerRef.current);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [minScale, maxScale]);

  return (
    <Box ref={outerRef} sx={{ height: fit.height, overflow: "hidden" }}>
      <Box
        ref={innerRef}
        sx={{
          transform: `scale(${fit.scale})`,
          transformOrigin: "top left",
          width: `${100 / fit.scale}%`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const TournamentDisplay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentOrg } = useOrganization();
  const bracketNames = getBracketNames(currentOrg?.slug);

  // Determine organization-specific theme (mirrors Layout.tsx / PublicLayout.tsx)
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const orgLogo = currentOrg?.branding?.logo || (isPadelEagle ? padelEagleLogo : splLogo);
  const orgColor = isPadelEagle ? "#743444" : "#1976d2";
  const orgAccent = isPadelEagle ? "#E49421" : "#e3f2fd";
  const orgAccentRgb = isPadelEagle ? "228,148,33" : "227,242,253";
  const orgName = isPadelEagle ? "Padel Eagle" : (currentOrg?.name || "SPL");
  const [searchParams] = useSearchParams();
  const mode = (searchParams.get("mode") || "slideshow") as DisplayMode;

  const [tournament, setTournament] = useState<Tournament | null>(null);

  // Group Standings only makes sense for formats that actually run a group stage.
  // Default to true before the tournament loads so the first render doesn't flicker.
  const hasGroupStage = tournament ? (tournament.groups?.length || 0) > 0 : true;

  // Parse per-slide config: "overview:15,standings:30,brackets:20"
  // "brackets" = both cups on one page (tabs/scroll); slideshow shows each cup on its own slide.
  const ALL_SLIDE_KEYS = ["overview", "schedule", "upcoming", "results", "standings", "brackets", "brackets-primary", "brackets-secondary"];
  const SLIDESHOW_DEFAULT_KEYS = ["overview", "schedule", "upcoming", "results", "standings", "brackets-primary", "brackets-secondary"];
  const slidesParam = searchParams.get("slides");
  // Legacy: fall back to slideTime param if new format not present
  const legacyTime = parseInt(searchParams.get("slideTime") || "15");
  const activeSlides: { key: string; duration: number }[] = (slidesParam
    ? slidesParam.split(",").flatMap(s => {
        const [key, dur] = s.split(":");
        if (!ALL_SLIDE_KEYS.includes(key)) return [];
        const duration = parseInt(dur) || 15;
        // Legacy URLs with a single "brackets" slide become one slide per cup
        return key === "brackets"
          ? [{ key: "brackets-primary", duration }, { key: "brackets-secondary", duration }]
          : [{ key, duration }];
      })
    : SLIDESHOW_DEFAULT_KEYS.map(key => ({ key, duration: legacyTime }))
  ).filter(s => hasGroupStage || s.key !== "standings");

  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const [paused, setPaused] = useState(false);
  const [timeLeft, setTimeLeft] = useState(activeSlides[0]?.duration ?? 15);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // All slides list kept for tab/scroll modes
  const slides = ALL_SLIDE_KEYS.filter(k => k !== "brackets-primary" && k !== "brackets-secondary").filter(k => hasGroupStage || k !== "standings");

  useEffect(() => {
    fetchTournament();
    const interval = setInterval(fetchTournament, 10000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (mode !== "slideshow" || activeSlides.length === 0) return;

    // Reset timer whenever the slide changes
    setTimeLeft(activeSlides[currentSlide]?.duration ?? 15);
  }, [mode, currentSlide, activeSlides.length]);

  useEffect(() => {
    if (mode !== "slideshow" || activeSlides.length === 0 || paused) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Advance slide
          setCurrentSlide(s => (s + 1) % activeSlides.length);
          return activeSlides[(currentSlide + 1) % activeSlides.length]?.duration ?? 15;
        }
        return prev - 1;
      });
    }, 1000);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [mode, currentSlide, activeSlides.length, paused]);

  const fetchTournament = async () => {
    try {
      const response = await apiRequest(`/api/tournaments/${id}`);
      const data = await response.json();
      setTournament(data);
    } catch (error) {
      console.error("Failed to fetch tournament:", error);
    }
  };

  const getTeamName = (teamId: string) => {
    if (!tournament) return "Unknown";
    const team = tournament.registeredTeams?.find((t) => t.id === teamId);
    if (!team) return "Unknown Team";
    if (team.player1Name && team.player2Name) {
      return `${team.player1Name} / ${team.player2Name}`;
    }
    return team.teamName || "Unknown Team";
  };

  // Get match label for knockout matches — matches the "Match #N" numbering on the card itself.
  const getMatchLabel = (matchNumber: number): string => {
    return `#${matchNumber + 1}`;
  };

  // Get team display name or placeholder for knockout matches
  const getKnockoutTeamDisplay = (match: KnockoutMatch, isTeam1: boolean): string => {
    const teamId = isTeam1 ? match.team1Id : match.team2Id;
    
    if (teamId) {
      return getTeamName(teamId);
    }
    
    // If no team yet, check for previous match reference FIRST (for specific match labels)
    const previousMatchNum = isTeam1 ? match.previousMatch1 : match.previousMatch2;
    if (previousMatchNum !== undefined && previousMatchNum !== null) {
      // Determine if this match uses losers or winners from previous matches
      // - Consolation-semi-finals: Losers of quarter-finals
      // - Third-place: Losers of semi-finals
      // - Seventh-place: Losers of consolation-semi-finals
      // - Fifth-place: Winners of consolation-semi-finals (NOT a loser match, despite isConsolationMatch being true)
      // - All other matches: Winners
      const isLoserMatch =
        match.round === 'consolation-semi-finals' ||
        match.round === 'seventh-place' ||
        match.round === 'third-place';
      
      const winnerOrLoser = isLoserMatch ? 'Loser' : 'Winner';
      console.log(`Match ${match.matchNumber}: previousMatch${isTeam1 ? '1' : '2'} = ${previousMatchNum}, showing ${winnerOrLoser} of Match ${getMatchLabel(previousMatchNum)}`);
      return `${winnerOrLoser} of Match ${getMatchLabel(previousMatchNum)}`;
    }
    
    // Check for placeholder text (only if no previousMatch reference)
    const placeholder = isTeam1 ? match.team1Placeholder : match.team2Placeholder;
    if (placeholder && !placeholder.toLowerCase().includes('previous match')) {
      // Only use placeholder if it's specific (not generic "winner of previous match")
      console.log(`Match ${match.matchNumber}: using placeholder = ${placeholder}`);
      return placeholder;
    }
    
    console.log(`Match ${match.matchNumber}: returning TBD - no teamId, previousMatch${isTeam1 ? '1' : '2'} = ${previousMatchNum}, placeholder = ${placeholder}`);
    return "TBD";
  };

  const getUpcomingMatches = () => {
    if (!tournament) return { currentSlot: null, upcomingSlots: [], numberOfCourts: 0 };
    const numberOfCourts = tournament.numberOfCourts || 4;

    interface MatchEntry {
      type: string;
      stage: string;
      team1: string;
      team2: string;
      time: string;
      courtNumber: number;
      isScored: boolean;
    }

    const allMatches: MatchEntry[] = [];

    // Group matches — include scored AND unscored so we can find the boundary.
    // A match is only "scored" if at least one game was played (team1Games + team2Games > 0).
    // The auto-schedule seeds matchResults with team1Games:0, team2Games:0 as defaults,
    // so checking !== null would falsely mark unplayed matches as scored.
    tournament.groups?.forEach((group) => {
      group.matchResults?.forEach((match) => {
        if (match.startTime && match.courtNumber) {
          const gamesPlayed = (match.team1Games || 0) + (match.team2Games || 0);
          allMatches.push({
            type: "Group",
            stage: group.name,
            team1: getTeamName(match.team1Id),
            team2: getTeamName(match.team2Id),
            time: match.startTime,
            courtNumber: match.courtNumber,
            isScored: gamesPlayed > 0,
          });
        }
      });
    });

    // Knockout matches — exclude placeholder slots (no real teams yet).
    // Let the time-ordering algorithm naturally place them after group matches.
    const mastersCup =
      tournament.knockoutBracket?.mastersCup || tournament.mastersCupBracket;
    const tourCup =
      tournament.knockoutBracket?.tourCup || tournament.tourCupBracket;

    const addKnockoutMatches = (bracket: KnockoutMatch[], cupName: string) => {
      bracket?.forEach((match) => {
        if (match.isPlaceholder || match.isCancelled || !match.startTime || !match.courtNumber) return;
        allMatches.push({
          type: cupName,
          stage: `${match.round} - Match ${getMatchLabel(match.matchNumber)}`,
          team1: getKnockoutTeamDisplay(match, true),
          team2: getKnockoutTeamDisplay(match, false),
          time: match.startTime,
          courtNumber: match.courtNumber,
          isScored: !!match.winnerId,
        });
      });
    };

    if (mastersCup) addKnockoutMatches(mastersCup, bracketNames.primary);
    if (tourCup) addKnockoutMatches(tourCup, bracketNames.secondary);

    if (allMatches.length === 0) return { currentSlot: null, upcomingSlots: [], numberOfCourts };

    // Sort by time then court number
    allMatches.sort((a, b) => {
      const timeDiff = new Date(a.time).getTime() - new Date(b.time).getTime();
      if (timeDiff !== 0) return timeDiff;
      return a.courtNumber - b.courtNumber;
    });

    // Group into time-keyed slots (Map insertion order = chronological)
    const timeSlotMap = new Map<string, MatchEntry[]>();
    allMatches.forEach((match) => {
      const key = new Date(match.time).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      if (!timeSlotMap.has(key)) timeSlotMap.set(key, []);
      timeSlotMap.get(key)!.push(match);
    });

    const sortedSlotKeys = Array.from(timeSlotMap.keys());

    // Find the last slot where EVERY match is scored
    let lastScoredSlotIdx = -1;
    sortedSlotKeys.forEach((key, idx) => {
      if (timeSlotMap.get(key)!.every((m) => m.isScored)) {
        lastScoredSlotIdx = idx;
      }
    });

    // "Currently Playing" = the first unscored slot after the last fully-scored one
    const currentSlotIdx = lastScoredSlotIdx + 1;
    if (currentSlotIdx >= sortedSlotKeys.length) {
      return { currentSlot: null, upcomingSlots: [], numberOfCourts };
    }

    const currentSlotKey = sortedSlotKeys[currentSlotIdx];
    const currentMatches = timeSlotMap.get(currentSlotKey)!.filter((m) => !m.isScored);

    // "Upcoming" = up to 2 subsequent unscored slots
    const upcomingSlots: Array<{ time: string; matches: MatchEntry[] }> = [];
    for (
      let i = currentSlotIdx + 1;
      i < sortedSlotKeys.length && upcomingSlots.length < 2;
      i++
    ) {
      const key = sortedSlotKeys[i];
      const unscored = timeSlotMap.get(key)!.filter((m) => !m.isScored);
      if (unscored.length > 0) upcomingSlots.push({ time: key, matches: unscored });
    }

    return {
      currentSlot: currentMatches.length > 0 ? { time: currentSlotKey, matches: currentMatches } : null,
      upcomingSlots,
      numberOfCourts,
    };
  };

  const getUniqueBracketRounds = (
    bracket: KnockoutMatch[],
    isConsolation = false,
  ) => {
    if (!bracket) return [];
    const rounds = Array.from(new Set(bracket.map((m) => m.round)));

    // Define order — early rounds must precede QF so they appear at the outer edges
    const mainOrder = [
      "Round 1", "Round 2", "Round 3", "Round 4", "Round 5",
      "round-of-32", "round-of-16",
      "quarter-finals", "semi-finals", "final",
    ];
    const consolationOrder = [
      "consolation-semi-finals",
      "seventh-place",
      "fifth-place",
      "third-place",
    ];
    const order = isConsolation ? consolationOrder : mainOrder;

    return rounds.sort((a, b) => {
      const aIdx = order.indexOf(a);
      const bIdx = order.indexOf(b);
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });
  };

  const getRoundMatches = (bracket: KnockoutMatch[], round: string) => {
    return bracket.filter((m) => m.round === round);
  };

  const renderBracketMatchCard = (match: KnockoutMatch, color: string) => {
    const big = mode === "slideshow";
    if ((match as any).isByeCard) {
      return (
        <Card
          key={match.id}
          variant="outlined"
          sx={{
            bgcolor: "rgba(255,255,255,0.03)",
            border: 2,
            borderStyle: "dashed",
            borderColor: "rgba(255,255,255,0.25)",
            width: "100%",
          }}
        >
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Typography
              variant="caption"
              sx={{ color: orgAccent, fontWeight: "bold", fontSize: "0.6rem" }}
            >
              Round 1
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 0.75,
                mt: 0.5,
                bgcolor: "rgba(255,255,255,0.08)",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold" noWrap sx={{ flex: 1, color: "#fff", fontSize: big ? "1.1rem" : "0.72rem", fontWeight: big ? 700 : undefined, lineHeight: 1.3 }}>
                {getTeamName((match as any).byeTeamId)}
              </Typography>
            </Box>
            <Chip
              label="BYE"
              size="small"
              sx={{ mt: 1, width: "100%", bgcolor: "rgba(116,52,68,0.7)", color: "#fff", fontWeight: "bold" }}
            />
            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 0.5, color: "rgba(255,255,255,0.6)", fontSize: "0.55rem" }}>
              Advances directly — no Round 1 opponent
            </Typography>
          </CardContent>
        </Card>
      );
    }
    return (
    <Card
      key={match.id}
      variant="outlined"
      sx={{
        bgcolor: match.isCancelled
          ? "rgba(255,255,255,0.03)"
          : match.winnerId
          ? `rgba(${orgAccentRgb},0.15)`
          : "rgba(255,255,255,0.05)",
        border: 2,
        borderColor: match.isCancelled ? "rgba(255,255,255,0.15)" : (match.winnerId ? orgAccent : orgColor),
        opacity: match.isCancelled ? 0.5 : 1,
        width: "100%",
      }}
    >
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: "wrap" }}>
          <Typography
            variant="caption"
            sx={{ color: orgAccent, fontWeight: "bold", fontSize: big ? "0.8rem" : "0.6rem", alignSelf: "center" }}
          >
            Match {getMatchLabel(match.matchNumber)}
          </Typography>
          {match.startTime && (
            <Chip
              label={new Date(match.startTime).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })}
              size="small"
              sx={{
                height: big ? 26 : 14,
                fontSize: big ? "1rem" : "0.55rem",
                bgcolor: orgAccent,
                color: "#1a1a1a",
                fontWeight: "bold",
              }}
            />
          )}
          {match.courtNumber && (
            <Chip
              label={big ? `Court ${match.courtNumber}` : `Ct ${match.courtNumber}`}
              size="small"
              sx={{
                height: big ? 26 : 14,
                fontSize: big ? "1rem" : "0.55rem",
                bgcolor: "rgba(116,52,68,0.7)",
                color: "#fff",
                fontWeight: "bold",
              }}
            />
          )}
        </Stack>
        <Stack spacing={0.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 0.75,
              bgcolor:
                match.winnerId === match.team1Id
                  ? `rgba(${orgAccentRgb},0.3)`
                  : "rgba(255,255,255,0.08)",
              borderRadius: 1,
              border: 1,
              borderColor:
                match.winnerId === match.team1Id
                  ? orgAccent
                  : "rgba(255,255,255,0.2)",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={match.winnerId === match.team1Id ? "bold" : "normal"}
              noWrap
              sx={{ flex: 1, color: "#fff", fontSize: big ? "1.1rem" : "0.72rem", fontWeight: big ? 700 : undefined, lineHeight: 1.3 }}
            >
              {getKnockoutTeamDisplay(match, true)}
            </Typography>
            {match.team1Games !== undefined && match.team1Games !== null && (
              <Typography
                fontWeight="bold"
                sx={{
                  ml: 0.5,
                  minWidth: 18,
                  textAlign: "center",
                  color: orgAccent,
                  fontSize: big ? "1.3rem" : "0.85rem",
                }}
              >
                {match.team1Games}
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            textAlign="center"
            sx={{ color: "#fff", fontWeight: "bold", fontSize: "0.6rem" }}
          >
            VS
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 0.75,
              bgcolor:
                match.winnerId === match.team2Id
                  ? `rgba(${orgAccentRgb},0.3)`
                  : "rgba(255,255,255,0.08)",
              borderRadius: 1,
              border: 1,
              borderColor:
                match.winnerId === match.team2Id
                  ? orgAccent
                  : "rgba(255,255,255,0.2)",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={match.winnerId === match.team2Id ? "bold" : "normal"}
              noWrap
              sx={{ flex: 1, color: "#fff", fontSize: big ? "1.1rem" : "0.72rem", fontWeight: big ? 700 : undefined, lineHeight: 1.3 }}
            >
              {getKnockoutTeamDisplay(match, false)}
            </Typography>
            {match.team2Games !== undefined && match.team2Games !== null && (
              <Typography
                fontWeight="bold"
                sx={{
                  ml: 0.5,
                  minWidth: 18,
                  textAlign: "center",
                  color: orgAccent,
                  fontSize: big ? "1.3rem" : "0.85rem",
                }}
              >
                {match.team2Games}
              </Typography>
            )}
          </Box>
        </Stack>
        {match.isCancelled && (
          <Chip
            label="✗ Cancelled"
            size="small"
            sx={{ mt: 1, width: "100%", bgcolor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)", textDecoration: "line-through" }}
          />
        )}
        {!match.isCancelled && (match.winnerId || match.isWalkover) && (
          <Chip
            label={match.isWalkover ? "Walkover (BYE)" : "✓ Complete"}
            sx={{
              mt: 1,
              width: "100%",
              bgcolor: orgAccent,
              color: "#fff",
              fontWeight: "bold",
            }}
            size="small"
          />
        )}
        {!match.isCancelled && !match.winnerId && match.team1Id && match.team2Id && (
          <Chip
            label="Pending"
            size="small"
            sx={{ mt: 1, width: "100%", bgcolor: orgColor, color: "#fff" }}
          />
        )}
      </CardContent>
    </Card>
    );
  };

  const renderMirrorBracket = (
    bracket: KnockoutMatch[],
    title: string,
    color: string,
    isConsolation = false,
  ) => {
    const rounds = getUniqueBracketRounds(bracket, isConsolation);

    // For consolation brackets, show them linearly
    if (isConsolation) {
      return (
        <Box sx={{ width: "100%", pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: "100%",
              alignItems: "flex-start",
            }}
          >
            {rounds.map((round) => {
              const roundMatches = getRoundMatches(bracket, round);
              if (roundMatches.length === 0) return null;

              return (
                <Box key={round} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    textAlign="center"
                    display="block"
                    mb={1}
                    textTransform="capitalize"
                    sx={{ color: orgAccent, fontSize: mode === "slideshow" ? "0.95rem" : "0.7rem" }}
                  >
                    {round.replace(/-/g, " ")}
                  </Typography>
                  <Stack spacing={1}>
                    {roundMatches.map((match) =>
                      renderBracketMatchCard(match, color),
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }

    // For main bracket, use mirror layout
    const finalRound = rounds[rounds.length - 1];
    const finalMatch = getRoundMatches(bracket, finalRound)[0];

    const leftSide = [];
    const rightSide = [];

    for (let i = 0; i < rounds.length - 1; i++) {
      const round = rounds[i];
      const roundMatches = getRoundMatches(bracket, round);
      const midpoint = Math.ceil(roundMatches.length / 2);

      leftSide.push({
        round,
        matches: roundMatches.slice(0, midpoint),
      });

      rightSide.push({
        round,
        matches: roundMatches.slice(midpoint),
      });
    }

    return (
      <Box sx={{ width: "100%", pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {leftSide.map((roundData) => (
            <Box key={`left-${roundData.round}`} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                textAlign="center"
                display="block"
                mb={1}
                textTransform="capitalize"
                sx={{ color: orgAccent, fontSize: mode === "slideshow" ? "0.95rem" : "0.7rem" }}
              >
                {roundData.round.replace(/-/g, " ")}
              </Typography>
              <Stack spacing={1}>
                {roundData.matches.map((match) =>
                  renderBracketMatchCard(match, color),
                )}
              </Stack>
            </Box>
          ))}

          {finalMatch && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                fontWeight="bold"
                textAlign="center"
                display="block"
                mb={1}
                textTransform="capitalize"
                sx={{ color: orgAccent, fontSize: mode === "slideshow" ? "0.95rem" : "0.7rem" }}
              >
                {finalRound.replace(/-/g, " ")}
              </Typography>
              {renderBracketMatchCard(finalMatch, color)}
            </Box>
          )}

          {rightSide
            .slice()
            .reverse()
            .map((roundData) => (
              <Box key={`right-${roundData.round}`} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  textAlign="center"
                  display="block"
                  mb={1}
                  textTransform="capitalize"
                  sx={{ color: orgAccent, fontSize: mode === "slideshow" ? "0.95rem" : "0.7rem" }}
                >
                  {roundData.round.replace(/-/g, " ")}
                </Typography>
                <Stack spacing={1}>
                  {roundData.matches.map((match) =>
                    renderBracketMatchCard(match, color),
                  )}
                </Stack>
              </Box>
            ))}
        </Box>
      </Box>
    );
  };

  const getRecentResults = () => {
    if (!tournament) return [];
    const results: any[] = [];

    // Group stage: only SCORED matches
    tournament.groups?.forEach((group) => {
      group.matchResults?.forEach((match) => {
        if (match.team1Games !== undefined && match.team1Games !== null) {
          results.push({
            type: "Group",
            stage: group.name,
            team1: getTeamName(match.team1Id),
            team2: getTeamName(match.team2Id),
            score: `${match.team1Games} - ${match.team2Games}`,
            time: match.startTime || null,
          });
        }
      });
    });

    // Knockout results
    const mastersCup =
      tournament.knockoutBracket?.mastersCup || tournament.mastersCupBracket;
    const tourCup =
      tournament.knockoutBracket?.tourCup || tournament.tourCupBracket;

    const addKnockoutResults = (bracket: KnockoutMatch[], cupName: string) => {
      bracket
        ?.filter((m) => m.winnerId && m.team1Id && m.team2Id)
        .forEach((match) => {
          results.push({
            type: cupName,
            stage: match.round,
            team1: getTeamName(match.team1Id!),
            team2: getTeamName(match.team2Id!),
            score: `${match.team1Games} - ${match.team2Games}`,
            winner: getTeamName(match.winnerId!),
            time: match.startTime || null,
          });
        });
    };

    if (mastersCup) addKnockoutResults(mastersCup, bracketNames.primary);
    if (tourCup) addKnockoutResults(tourCup, bracketNames.secondary);

    // Sort by time, most recent first
    results.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return new Date(b.time).getTime() - new Date(a.time).getTime();
    });

    return results.slice(0, 10); // Show 10 most recent
  };

  const renderSchedule = () => {
    if (!tournament?.startTime) {
      return (
        <Fade in timeout={800}>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h4" sx={{ color: orgAccent, mb: 2 }}>
              No Schedule Available
            </Typography>
            <Typography variant="h6" sx={{ color: "#fff" }}>
              Tournament schedule will be available once timing is set
            </Typography>
          </Box>
        </Fade>
      );
    }

    const numberOfCourts = tournament.numberOfCourts || 4;
    
    // Build schedule items from the tournament
    const scheduleItems: Array<{
      time: string;
      type: "match" | "event" | "block";
      title: string;
      subtitle: string;
      courtNumber?: number;
      eventType?: string;
      isScored?: boolean;
      score?: string;
    }> = [];

    // Add events
    if (tournament.events) {
      tournament.events.forEach((event) => {
        scheduleItems.push({
          time: event.startTime,
          type: "event",
          title: event.name,
          subtitle: event.type.charAt(0).toUpperCase() + event.type.slice(1),
          eventType: event.type,
        });
      });
    }

    // Add group matches
    if (tournament.groups) {
      tournament.groups.forEach((group, groupIndex) => {
        if (group.matchResults) {
          group.matchResults.forEach((match, matchIndex) => {
            if (match.startTime && match.courtNumber) {
              const isScored = match.team1Games > 0 || match.team2Games > 0;
              const score = isScored ? `${match.team1Games}-${match.team2Games}` : undefined;
              
              scheduleItems.push({
                time: match.startTime,
                type: "match",
                title: `${getTeamName(match.team1Id)} vs ${getTeamName(match.team2Id)}`,
                subtitle: `Group ${String.fromCharCode(65 + groupIndex)}`,
                courtNumber: match.courtNumber,
                isScored,
                score,
              });
            }
          });
        }
      });
    }

    // Add knockout matches
    if (tournament.knockoutBracket?.mastersCup) {
      tournament.knockoutBracket.mastersCup.forEach((match) => {
        if (match.isCancelled) return;
        if (match.startTime && match.courtNumber) {
          const team1Display = getKnockoutTeamDisplay(match, true);
          const team2Display = getKnockoutTeamDisplay(match, false);
          const isScored = !!match.winnerId;
          const score = isScored && match.team1Games !== undefined && match.team2Games !== undefined
            ? `${match.team1Games}-${match.team2Games}`
            : undefined;
          
          scheduleItems.push({
            time: match.startTime,
            type: "match",
            title: `${team1Display} vs ${team2Display}`,
            subtitle: `${bracketNames.primary} - Match ${getMatchLabel(match.matchNumber)}`,
            courtNumber: match.courtNumber,
            isScored,
            score,
          });
        }
      });
    }

    if (tournament.knockoutBracket?.tourCup) {
      tournament.knockoutBracket.tourCup.forEach((match) => {
        if (match.isCancelled) return;
        if (match.startTime && match.courtNumber) {
          const team1Display = getKnockoutTeamDisplay(match, true);
          const team2Display = getKnockoutTeamDisplay(match, false);
          const isScored = !!match.winnerId;
          const score = isScored && match.team1Games !== undefined && match.team2Games !== undefined
            ? `${match.team1Games}-${match.team2Games}`
            : undefined;
          
          scheduleItems.push({
            time: match.startTime,
            type: "match",
            title: `${team1Display} vs ${team2Display}`,
            subtitle: `${bracketNames.secondary} - Match ${getMatchLabel(match.matchNumber)}`,
            courtNumber: match.courtNumber,
            isScored,
            score,
          });
        }
      });
    }

    // Add court blocks (manually placed, occupy a single court like a match)
    if (tournament.scheduleBlocks) {
      tournament.scheduleBlocks.forEach((block) => {
        scheduleItems.push({
          time: block.startTime,
          type: "block",
          title: block.name,
          subtitle: "Blocked",
          courtNumber: block.courtNumber,
        });
      });
    }

    const formatTime = (time: string) => {
      // Handle ISO date strings
      if (time.includes("T")) {
        return new Date(time).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      }
      // Handle HH:MM format - return as is
      if (/^\d{2}:\d{2}$/.test(time)) {
        return time;
      }
      // Fallback: try to parse as date
      const date = new Date(time);
      if (isNaN(date.getTime())) {
        console.warn("Invalid time format:", time);
        return "00:00";
      }
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };

    const getEventColor = (type: string) => {
      switch (type) {
        case "break":
          return isPadelEagle ? "#E49421" : "#ff9800";
        case "ceremony":
          return isPadelEagle ? "#F4C48B" : "#9c27b0";
        case "tombola":
          return isPadelEagle ? "#D8C4C1" : "#e91e63";
        default:
          return isPadelEagle ? "#B79498" : "#607d8b";
      }
    };

    // Build grid structure
    const matches = scheduleItems.filter(item => (item.type === "match" || item.type === "block") && item.courtNumber);
    const events = scheduleItems.filter(item => item.type === "event");
    
    // Get unique time slots from both matches AND events
    const timeSlots = new Set<string>();
    matches.forEach(match => {
      timeSlots.add(formatTime(match.time));
    });
    events.forEach(event => {
      timeSlots.add(formatTime(event.time));
    });
    
    const sortedTimeSlots = Array.from(timeSlots).sort();
    
    // Create grid: Map<time, Map<court, match>>
    const grid = new Map<string, Map<number, typeof scheduleItems[0]>>();
    const eventMap = new Map<string, typeof scheduleItems[0]>();
    
    sortedTimeSlots.forEach(time => {
      grid.set(time, new Map());
    });
    
    matches.forEach(match => {
      const time = formatTime(match.time);
      const court = match.courtNumber!;
      grid.get(time)?.set(court, match);
    });
    
    events.forEach(event => {
      const time = formatTime(event.time);
      eventMap.set(time, event);
    });

    // Live slideshow: show a readable window instead of every slot. It starts a couple of rows
    // before the first unscored match and follows progress as matches get scored.
    const SCHEDULE_MAX_ROWS = 9;
    const SCHEDULE_CONTEXT_ROWS = 2;
    const slotHasPending = (time: string) =>
      Array.from(grid.get(time)?.values() || []).some((m) => m.type === "match" && !m.isScored);
    let visibleTimeSlots = sortedTimeSlots;
    let hiddenBefore = 0;
    let hiddenAfter = 0;
    if (mode === "slideshow" && sortedTimeSlots.length > SCHEDULE_MAX_ROWS) {
      let firstPending = sortedTimeSlots.findIndex(slotHasPending);
      if (firstPending === -1) firstPending = sortedTimeSlots.length;
      const startIdx = Math.max(
        0,
        Math.min(firstPending - SCHEDULE_CONTEXT_ROWS, sortedTimeSlots.length - SCHEDULE_MAX_ROWS),
      );
      visibleTimeSlots = sortedTimeSlots.slice(startIdx, startIdx + SCHEDULE_MAX_ROWS);
      hiddenBefore = startIdx;
      hiddenAfter = sortedTimeSlots.length - startIdx - SCHEDULE_MAX_ROWS;
    }

    const scheduleBody = (
      <Fade in timeout={800}>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: mode === "slideshow" ? 2 : 4,
              gap: 2,
            }}
          >
            <img src={orgLogo} alt={orgName} style={{ height: 60 }} />
            <Typography
              variant="h3"
              sx={{ fontWeight: "bold", color: orgAccent }}
            >
              📅 Day Schedule
            </Typography>
          </Box>

          {/* Court Grid */}
          {sortedTimeSlots.length > 0 ? (
            <Box>
              <Typography variant="h5" sx={{ color: orgAccent, mb: 2, fontWeight: "bold" }}>
                Court Schedule
                {(hiddenBefore > 0 || hiddenAfter > 0) && (
                  <Typography component="span" sx={{ ml: 2, color: "rgba(255,255,255,0.6)", fontSize: "1rem", fontWeight: 400 }}>
                    {hiddenBefore > 0 && `▲ ${hiddenBefore} earlier`}
                    {hiddenBefore > 0 && hiddenAfter > 0 && "  ·  "}
                    {hiddenAfter > 0 && `▼ ${hiddenAfter} later`}
                  </Typography>
                )}
              </Typography>
              
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `120px repeat(${numberOfCourts}, 1fr)`,
                  gap: 1,
                }}
              >
                {/* Header Row */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: orgColor,
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                    textAlign: "center",
                    borderRadius: 1,
                  }}
                >
                  Time
                </Box>
                {Array.from({ length: numberOfCourts }, (_, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 2,
                      bgcolor: orgColor,
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1.2rem",
                      textAlign: "center",
                      borderRadius: 1,
                    }}
                  >
                    Court {i + 1}
                  </Box>
                ))}

                {/* Grid Rows */}
                {visibleTimeSlots.map((time) => {
                  const courtMatches = grid.get(time);
                  const event = eventMap.get(time);
                  
                  // If this time slot has an event, render it spanning all columns
                  if (event) {
                    return (
                      <React.Fragment key={time}>
                        {/* Time Cell */}
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "rgba(255,255,255,0.1)",
                            fontWeight: "bold",
                            fontSize: "1.3rem",
                            color: getEventColor(event.eventType || ""),
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 1,
                          }}
                        >
                          {time}
                        </Box>
                        
                        {/* Event spanning all courts */}
                        <Box
                          sx={{
                            gridColumn: `span ${numberOfCourts}`,
                            p: 2,
                            borderRadius: 1,
                          }}
                        >
                          <Card
                            sx={{
                              bgcolor: isPadelEagle ? "rgba(244,196,139,0.2)" : "rgba(156, 39, 176, 0.3)",
                              border: `3px solid ${getEventColor(event.eventType || "")}`,
                            }}
                          >
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                  sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: "50%",
                                    bgcolor: getEventColor(event.eventType || ""),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "1.5rem",
                                  }}
                                >
                                  {event.eventType === "break" && "☕"}
                                  {event.eventType === "ceremony" && "🏆"}
                                  {event.eventType === "tombola" && "🎫"}
                                  {(!event.eventType || event.eventType === "other") && "📋"}
                                </Box>
                                <Box>
                                  <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                                    {event.title}
                                  </Typography>
                                  <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.8)" }}>
                                    {event.subtitle}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Box>
                      </React.Fragment>
                    );
                  }
                  
                  // Regular match row
                  return (
                    <React.Fragment key={time}>
                      {/* Time Cell */}
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "rgba(255,255,255,0.1)",
                          fontWeight: "bold",
                          fontSize: "1.3rem",
                          color: orgAccent,
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 1,
                        }}
                      >
                        {time}
                      </Box>

                      {/* Court Cells */}
                      {Array.from({ length: numberOfCourts }, (_, courtIndex) => {
                        const courtNumber = courtIndex + 1;
                        const match = courtMatches?.get(courtNumber);

                        return (
                          <Box
                            key={courtNumber}
                            sx={{
                              p: 1,
                              bgcolor: match ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.3)",
                              minHeight: isPadelEagle ? (mode === "slideshow" ? 135 : 150) : (mode === "slideshow" ? 70 : 100),
                              borderRadius: 1,
                              display: "flex",
                              alignItems: isPadelEagle ? "stretch" : "center",
                              justifyContent: "center",
                            }}
                          >
                            {match ? (
                              <Card
                                sx={{
                                  width: "100%",
                                  ...(isPadelEagle && { height: "100%", display: "flex", alignItems: "center" }),
                                  bgcolor: match.type === "block"
                                    ? (isPadelEagle ? "#2E151B" : "rgba(60, 60, 60, 0.7)")
                                    : (match.isScored ? (isPadelEagle ? "#74586C" : "rgba(46, 125, 50, 0.7)") : (isPadelEagle ? "#743444" : "rgba(116,52,68, 0.6)")),
                                  border: match.type === "block"
                                    ? (isPadelEagle ? "2px dashed #B79498" : "2px dashed #9e9e9e")
                                    : (match.isScored ? (isPadelEagle ? "2px solid #F4C48B" : "2px solid #4caf50") : `2px solid ${orgAccent}`),
                                }}
                              >
                                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, ...(isPadelEagle && { width: "100%" }) }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ color: "#fff", fontWeight: "bold", fontSize: mode === "slideshow" ? "1.25rem" : "0.95rem", lineHeight: 1.25, mb: 0.5 }}
                                  >
                                    {match.title}
                                  </Typography>
                                  <Chip
                                    label={match.subtitle}
                                    size="small"
                                    sx={{
                                      bgcolor: match.type === "block" ? (isPadelEagle ? "#B79498" : "#9e9e9e") : (match.isScored ? (isPadelEagle ? "#F4C48B" : "#4caf50") : orgAccent),
                                      color: "#1a1a1a",
                                      fontWeight: "bold",
                                      fontSize: mode === "slideshow" ? "0.65rem" : "0.75rem",
                                      height: mode === "slideshow" ? 18 : undefined,
                                    }}
                                  />
                                  {match.isScored && match.score && (
                                    <Typography
                                      variant="h5"
                                      sx={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        textAlign: "center",
                                        mt: 1,
                                      }}
                                    >
                                      {match.score}
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            ) : (
                              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.3)" }}>
                                —
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.5)" }}>
                No matches scheduled yet
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>
    );
    // In the live slideshow the day schedule must fit on one screen without scrolling
    return mode === "slideshow" ? <FitToScreen minScale={0.75}>{scheduleBody}</FitToScreen> : scheduleBody;
  };

  const renderOverview = () => (
    <Fade in timeout={800}>
      <Box>
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <img
            src={orgLogo}
            alt={orgName}
            style={{ height: 180, marginBottom: 24 }}
          />
          <Typography
            variant="h2"
            sx={{ fontWeight: "bold", mb: 2, color: orgAccent }}
          >
            {tournament?.name}
          </Typography>
          <Chip
            label={tournament?.status?.toUpperCase()}
            sx={{
              fontSize: "1.2rem",
              px: 3,
              py: 2,
              height: "auto",
              bgcolor: orgColor,
              color: "#fff",
              fontWeight: "bold",
            }}
          />
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Card
              sx={{
                bgcolor: `rgba(${orgAccentRgb},0.15)`,
                border: `3px solid ${orgAccent}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ color: orgAccent, mb: 2, fontWeight: "bold" }}
                >
                  <TrophyIcon
                    sx={{ fontSize: 40, mr: 1, verticalAlign: "middle" }}
                  />
                  {bracketNames.primary}
                </Typography>
                <Typography variant="h6" sx={{ color: "#fff" }}>
                  {tournament?.groups?.reduce(
                    (sum, g) => sum + (g.teamIds?.length || 0),
                    0,
                  ) || 0}{" "}
                  Teams Competing
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card
              sx={{
                bgcolor: "rgba(116,52,68,0.2)",
                border: `3px solid ${orgColor}`,
              }}
            >
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ color: orgAccent, mb: 2, fontWeight: "bold" }}
                >
                  <TrophyIcon
                    sx={{ fontSize: 40, mr: 1, verticalAlign: "middle" }}
                  />
                  {bracketNames.secondary}
                </Typography>
                <Typography variant="h6" sx={{ color: "#fff" }}>
                  {tournament?.groups?.reduce(
                    (sum, g) => sum + (g.teamIds?.length || 0),
                    0,
                  ) || 0}{" "}
                  Teams Competing
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Sponsors */}
        {Array.isArray((tournament as any)?.sponsors) && (tournament as any).sponsors.length > 0 && (
          <Box sx={{ mt: 6, textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 600, mb: 3, letterSpacing: "0.15em", textTransform: "uppercase", fontSize: "1rem" }}
            >
              Our Sponsors
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              {(tournament as any).sponsors.map((s: any, i: number) => (
                <Box
                  key={i}
                  component={s.websiteUrl ? "a" : "div"}
                  href={s.websiteUrl || undefined}
                  target={s.websiteUrl ? "_blank" : undefined}
                  rel={s.websiteUrl ? "noopener noreferrer" : undefined}
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, textDecoration: "none" }}
                >
                  <Box
                    component="img"
                    src={s.logoUrl}
                    alt={s.name}
                    sx={{
                      height: 60,
                      maxWidth: 180,
                      objectFit: "contain",
                      bgcolor: "#fff",
                      borderRadius: 1,
                      p: 1,
                      opacity: 0.85,
                      transition: "opacity 0.2s",
                      "&:hover": { opacity: 1 },
                    }}
                  />
                  {s.name && (
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", letterSpacing: "0.05em" }}>
                      {s.name}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  );

  const renderUpcoming = () => {
    const { currentSlot, upcomingSlots, numberOfCourts } = getUpcomingMatches();
    const hasAnything = currentSlot || upcomingSlots.length > 0;

    const renderSlotGrid = (
      time: string,
      matches: any[],
      label: string,
      labelColor: string,
      borderColor: string,
    ) => (
      <Box key={time}>
        <Typography
          variant="h5"
          sx={{ color: labelColor, fontWeight: "bold", mb: 2, textAlign: "center" }}
        >
          {label} — {time}
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          {Array.from({ length: numberOfCourts }, (_, i) => i + 1).map((courtNum) => {
            const match = matches.find((m: any) => m.courtNumber === courtNum);
            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={Math.max(2, Math.floor(12 / numberOfCourts))}
                key={courtNum}
              >
                <Card
                  sx={{
                    bgcolor: match ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.03)",
                    border: match ? `2px solid ${borderColor}` : "1px dashed rgba(116,52,68,0.4)",
                    opacity: match ? 1 : 0.5,
                    height: "100%",
                    boxSizing: "border-box",
                    minHeight: 300,
                  }}
                >
                  <CardContent>
                    <Chip
                      label={`Court ${courtNum}`}
                      sx={{
                        fontWeight: "bold",
                        bgcolor: match ? `rgba(${orgAccentRgb},0.3)` : "rgba(255,255,255,0.1)",
                        color: match ? orgAccent : "rgba(255,255,255,0.3)",
                        border: match ? `1px solid ${orgAccent}` : "1px solid rgba(255,255,255,0.1)",
                        mb: 2,
                      }}
                    />
                    {match ? (
                      <>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
                        >
                          <Chip
                            label={match.type}
                            size="small"
                            sx={{ fontWeight: "bold", bgcolor: orgColor, color: "#fff" }}
                          />
                          <Chip
                            label={match.stage}
                            size="small"
                            sx={{
                              bgcolor: "rgba(116,52,68,0.5)",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        </Stack>
                        <Typography
                          variant="h6"
                          sx={{ mt: 1, textAlign: "center", color: "#fff", fontWeight: "bold" }}
                        >
                          {match.team1}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: "center",
                            color: orgAccent,
                            fontWeight: "bold",
                            fontSize: "0.9rem",
                          }}
                        >
                          VS
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ textAlign: "center", color: "#fff", fontWeight: "bold" }}
                        >
                          {match.team2}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          textAlign: "center",
                          color: "rgba(255,255,255,0.3)",
                          fontStyle: "italic",
                          mt: 3,
                        }}
                      >
                        No match
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );

    return (
      <Fade in timeout={800}>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 4,
              gap: 2,
            }}
          >
            <img src={orgLogo} alt={orgName} style={{ height: 60 }} />
            <Typography variant="h3" sx={{ fontWeight: "bold", color: orgAccent }}>
              <ScoreIcon sx={{ fontSize: 50, mr: 2, verticalAlign: "middle" }} />
              Current & Upcoming Matches
            </Typography>
          </Box>
          {!hasAnything ? (
            <Typography variant="h5" sx={{ textAlign: "center", py: 8, color: "#fff" }}>
              No upcoming matches
            </Typography>
          ) : (
            <Stack spacing={5}>
              {currentSlot &&
                renderSlotGrid(
                  currentSlot.time,
                  currentSlot.matches,
                  "🎾 Currently Playing",
                  orgAccent,
                  orgAccent,
                )}
              {upcomingSlots[0] &&
                renderSlotGrid(
                  upcomingSlots[0].time,
                  upcomingSlots[0].matches,
                  "⏭ Up Next",
                  `rgba(${orgAccentRgb},0.8)`,
                  orgColor,
                )}
              {upcomingSlots[1] &&
                renderSlotGrid(
                  upcomingSlots[1].time,
                  upcomingSlots[1].matches,
                  "On Deck",
                  `rgba(${orgAccentRgb},0.6)`,
                  orgColor,
                )}
            </Stack>
          )}
        </Box>
      </Fade>
    );
  };

  const renderResults = () => {
    const results = getRecentResults();
    return (
      <Fade in timeout={800}>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 4,
              gap: 2,
            }}
          >
            <img
              src={orgLogo}
              alt={orgName}
              style={{ height: 60 }}
            />
            <Typography
              variant="h3"
              sx={{ fontWeight: "bold", color: orgAccent }}
            >
              Recent Results
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              bgcolor: "rgba(255,255,255,0.1)",
              border: `2px solid ${orgColor}`,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: orgColor }}>
                  <TableCell
                    sx={{
                      fontSize: "1.2rem",
                      color: "#fff",
                      fontWeight: "bold",
                      borderBottom: `2px solid ${orgAccent}`,
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "1.2rem",
                      color: "#fff",
                      fontWeight: "bold",
                      borderBottom: `2px solid ${orgAccent}`,
                    }}
                  >
                    Stage
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "1.2rem",
                      color: "#fff",
                      fontWeight: "bold",
                      borderBottom: `2px solid ${orgAccent}`,
                    }}
                  >
                    Match
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "1.2rem",
                      color: "#fff",
                      fontWeight: "bold",
                      borderBottom: `2px solid ${orgAccent}`,
                    }}
                  >
                    Score
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:nth-of-type(odd)": {
                        bgcolor: "rgba(255,255,255,0.05)",
                      },
                    }}
                  >
                    <TableCell
                      sx={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
                    >
                      <Chip
                        label={result.type}
                        size="small"
                        sx={{ bgcolor: orgColor, color: "#fff" }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "1.1rem",
                        color: "#fff",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {result.stage}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "1.1rem",
                        color: "#fff",
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {result.team1} vs {result.team2}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                        color: orgAccent,
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {result.score}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Fade>
    );
  };

  const renderStandings = () => (
    <Fade in timeout={800}>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
            gap: 2,
          }}
        >
          <img src={orgLogo} alt={orgName} style={{ height: 60 }} />
          <Typography
            variant="h3"
            sx={{ fontWeight: "bold", color: orgAccent }}
          >
            Group Standings
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {tournament?.groups?.map((group) => (
            <Grid item xs={12} md={6} key={group.id}>
              <Card
                sx={{
                  bgcolor: "rgba(255,255,255,0.1)",
                  border: `2px solid ${orgColor}`,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h4"
                    sx={{ mb: 2, color: orgAccent, fontWeight: "bold" }}
                  >
                    {group.name}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: orgColor }}>
                          <TableCell
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            Pos
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            Team
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            W
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            D
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            L
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            GW
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            GD
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: "bold",
                              borderBottom: `2px solid ${orgAccent}`,
                            }}
                          >
                            Pts
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(group.standings && group.standings.length > 0
                          ? [...group.standings].sort((a, b) => {
                              if (b.points !== a.points) return b.points - a.points;
                              if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
                              return b.gamesWon - a.gamesWon;
                            })
                          : (group.teamIds || []).map((teamId) => ({
                              teamId,
                              wins: 0, draws: 0, losses: 0,
                              points: 0, gamesWon: 0, gamesLost: 0, gamesDiff: 0,
                              matchesPlayed: 0,
                            }))
                        ).map((standing, idx) => (
                            <TableRow
                              key={standing.teamId}
                              sx={{
                                "&:nth-of-type(odd)": {
                                  bgcolor: "rgba(255,255,255,0.05)",
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  fontSize: "1.1rem",
                                  fontWeight: "bold",
                                  color: orgAccent,
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {idx + 1}
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#fff",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {getTeamName(standing.teamId)}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#fff",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {standing.wins}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#fff",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {standing.draws}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#fff",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {standing.losses}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#fff",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {standing.gamesWon}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: "1.1rem",
                                  color: "#fff",
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {standing.gamesDiff > 0 ? "+" : ""}
                                {standing.gamesDiff}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{
                                  fontSize: "1.2rem",
                                  fontWeight: "bold",
                                  color: orgAccent,
                                  borderBottom:
                                    "1px solid rgba(255,255,255,0.1)",
                                }}
                              >
                                {standing.points}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Fade>
  );

  const renderBrackets = (which: "both" | "primary" | "secondary" = "both") => {
    const showMasters = which !== "secondary";
    const showTour = which !== "primary";
    const mastersCupRaw =
      tournament?.knockoutBracket?.mastersCup || tournament?.mastersCupBracket;
    const tourCupRaw =
      tournament?.knockoutBracket?.tourCup || tournament?.tourCupBracket;
    const mastersCup = mastersCupRaw ? withByeCards(mastersCupRaw) : mastersCupRaw;
    const tourCup = tourCupRaw ? withByeCards(tourCupRaw) : tourCupRaw;

    // Split into main and consolation brackets
    const mastersCupMain = mastersCup?.filter(
      (m) =>
        !m.round.includes("consolation") &&
        m.round !== "third-place" &&
        m.round !== "fifth-place" &&
        m.round !== "seventh-place",
    );
    const mastersCupConsolation = mastersCup?.filter(
      (m) =>
        m.round.includes("consolation") ||
        m.round === "third-place" ||
        m.round === "fifth-place" ||
        m.round === "seventh-place",
    );

    const tourCupMain = tourCup?.filter(
      (m) =>
        !m.round.includes("consolation") &&
        m.round !== "third-place" &&
        m.round !== "fifth-place" &&
        m.round !== "seventh-place",
    );
    const tourCupConsolation = tourCup?.filter(
      (m) =>
        m.round.includes("consolation") ||
        m.round === "third-place" ||
        m.round === "fifth-place" ||
        m.round === "seventh-place",
    );

    // Only show brackets when at least one match has real seeded teams (i.e. knockout is actually generated)
    const hasRealBrackets =
      mastersCup?.some((m) => m.team1Id && m.team2Id) ||
      tourCup?.some((m) => m.team1Id && m.team2Id);

    if (!hasRealBrackets) {
      return (
        <Fade in timeout={800}>
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
                gap: 2,
              }}
            >
              <img src={orgLogo} alt={orgName} style={{ height: 60 }} />
              <Typography variant="h3" sx={{ fontWeight: "bold", color: orgAccent }}>
                Knockout Brackets
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.5)", mb: 2 }}>
              {hasGroupStage ? "Group stage in progress" : "Bracket not generated yet"}
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.35)" }}>
              {hasGroupStage
                ? "Knockout brackets will be available once the group stage is complete"
                : "The knockout bracket will appear here once it's generated"}
            </Typography>
          </Box>
        </Fade>
      );
    }

    const bracketsContent = (
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: which === "both" ? 4 : 2,
              gap: 2,
            }}
          >
            <img
              src={orgLogo}
              alt={orgName}
              style={{ height: 60 }}
            />
            <Typography
              variant="h3"
              sx={{ fontWeight: "bold", color: orgAccent }}
            >
              Knockout Brackets
            </Typography>
          </Box>

          <Stack spacing={which === "both" ? 6 : 3}>
            {/* Masters Cup Main Bracket */}
            {showMasters && mastersCupMain && mastersCupMain.length > 0 && (
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    fontWeight: "bold",
                    color: orgAccent,
                    textAlign: "center",
                  }}
                >
                  🏆 {bracketNames.primary}
                </Typography>
                {renderMirrorBracket(
                  mastersCupMain,
                  bracketNames.primary,
                  orgAccent,
                  false,
                )}
              </Box>
            )}

            {/* Masters Cup Consolation */}
            {showMasters && mastersCupConsolation && mastersCupConsolation.length > 0 && (
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    fontWeight: "bold",
                    color: orgAccent,
                    textAlign: "center",
                  }}
                >
                  {bracketNames.primary} - Position Playoffs
                </Typography>
                {renderMirrorBracket(
                  mastersCupConsolation,
                  "Position Playoffs",
                  orgAccent,
                  true,
                )}
              </Box>
            )}

            {which === "both" && <Divider sx={{ my: 4, borderColor: orgColor, borderWidth: 2 }} />}

            {/* Tour Cup Main Bracket */}
            {showTour && tourCupMain && tourCupMain.length > 0 && (
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    fontWeight: "bold",
                    color: orgAccent,
                    textAlign: "center",
                  }}
                >
                  🏆 {bracketNames.secondary}
                </Typography>
                {renderMirrorBracket(tourCupMain, bracketNames.secondary, orgColor, false)}
              </Box>
            )}

            {/* Tour Cup Consolation */}
            {showTour && tourCupConsolation && tourCupConsolation.length > 0 && (
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    fontWeight: "bold",
                    color: orgAccent,
                    textAlign: "center",
                  }}
                >
                  {bracketNames.secondary} - Position Playoffs
                </Typography>
                {renderMirrorBracket(
                  tourCupConsolation,
                  "Position Playoffs",
                  orgColor,
                  true,
                )}
              </Box>
            )}
          </Stack>
        </Box>
    );

    return (
      <Fade in timeout={800}>
        {mode === "slideshow" && which !== "both" ? (
          <Box>
            <FitToScreen minScale={0.55} maxScale={1.4}>{bracketsContent}</FitToScreen>
          </Box>
        ) : (
          bracketsContent
        )}
      </Fade>
    );
  };

  const renderContent = () => {
    if (mode === "slideshow") {
      const slideRenderMap: Record<string, () => React.ReactNode> = {
        overview:  renderOverview,
        schedule:  renderSchedule,
        upcoming:  renderUpcoming,
        results:   renderResults,
        standings: renderStandings,
        brackets:  () => renderBrackets("both"),
        "brackets-primary":   () => renderBrackets("primary"),
        "brackets-secondary": () => renderBrackets("secondary"),
      };
      const currentKey = activeSlides[currentSlide]?.key;
      const currentDuration = activeSlides[currentSlide]?.duration ?? 15;
      const progress = ((currentDuration - timeLeft) / currentDuration) * 100;

      return (
        <Box sx={{ position: "relative", pb: 6 }}>
          {currentKey && slideRenderMap[currentKey]?.()}

          {/* Countdown bar fixed at the bottom */}
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: "rgba(0,0,0,0.7)",
              px: 3,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 2,
              zIndex: 1000,
            }}
          >
            <Typography sx={{ color: orgAccent, fontWeight: 700, minWidth: 120, fontSize: "0.85rem" }}>
              {activeSlides[currentSlide]?.key?.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.2)",
                "& .MuiLinearProgress-bar": { bgcolor: orgAccent, borderRadius: 3 },
              }}
            />
            <Typography sx={{ color: "#fff", minWidth: 40, textAlign: "right", fontSize: "0.85rem" }}>
              {paused ? "Paused" : `${timeLeft}s`}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                size="small"
                aria-label="Previous slide"
                onClick={() => setCurrentSlide(s => (s - 1 + activeSlides.length) % activeSlides.length)}
                sx={{ color: "#fff" }}
              >
                <SkipPreviousIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={paused ? "Resume slideshow" : "Pause slideshow"}
                onClick={() => setPaused(p => !p)}
                sx={{ color: "#fff" }}
              >
                {paused ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                aria-label="Next slide"
                onClick={() => setCurrentSlide(s => (s + 1) % activeSlides.length)}
                sx={{ color: "#fff" }}
              >
                <SkipNextIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {activeSlides.map((s, i) => (
                <Box
                  key={s.key}
                  onClick={() => setCurrentSlide(i)}
                  sx={{
                    width: 8, height: 8, borderRadius: "50%",
                    bgcolor: i === currentSlide ? orgAccent : "rgba(255,255,255,0.3)",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      );
    }

    if (mode === "tabs") {
      // Built conditionally (rather than a fixed index switch) so tab labels and their
      // panels stay aligned when Standings is dropped for formats with no group stage.
      const tabPanels: { key: string; label: string; render: () => React.ReactNode }[] = [
        { key: "overview", label: "Overview", render: renderOverview },
        { key: "schedule", label: "Schedule", render: renderSchedule },
        { key: "upcoming", label: "Upcoming", render: renderUpcoming },
        { key: "results", label: "Results", render: renderResults },
        { key: "standings", label: "Standings", render: renderStandings },
        { key: "brackets", label: "Brackets", render: () => renderBrackets("both") },
      ].filter(t => hasGroupStage || t.key !== "standings");

      return (
        <Box>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
            sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
          >
            {tabPanels.map(t => <Tab key={t.key} label={t.label} sx={{ fontSize: "1.2rem" }} />)}
          </Tabs>
          {tabPanels[currentTab]?.render()}
        </Box>
      );
    }

    // Scroll mode - all sections
    return (
      <Stack spacing={6}>
        {renderOverview()}
        <Divider />
        {renderSchedule()}
        <Divider />
        {renderUpcoming()}
        <Divider />
        {renderResults()}
        {hasGroupStage && (
          <>
            <Divider />
            {renderStandings()}
          </>
        )}
        <Divider />
        {renderBrackets("both")}
      </Stack>
    );
  };

  if (!tournament) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#1a1a1a",
          backgroundColor: isPadelEagle ? "#2E151B" : "#2d1b26",
        }}
      >
        <Stack alignItems="center" spacing={3}>
          <img src={orgLogo} alt={orgName} style={{ height: 150 }} />
          <Typography variant="h3" sx={{ color: orgAccent }}>
            Loading...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#1a1a1a",
        color: "#fff",
        p: 4,
        backgroundColor: isPadelEagle ? "#2E151B" : "#2d1b26",
      }}
    >
      <Box sx={{ maxWidth: "1920px", mx: "auto" }}>{renderContent()}</Box>

      {/* Mode indicator */}
      <Box
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          bgcolor: orgColor,
          px: 2,
          py: 1,
          borderRadius: 1,
          border: `1px solid ${orgAccent}`,
        }}
      >
        <Typography variant="caption">
          Mode: {mode}{" "}
          {mode === "slideshow" && `(${currentSlide + 1}/${activeSlides.length})`}
        </Typography>
      </Box>
    </Box>
  );
};

export default TournamentDisplay;
